import React, { ReactElement, useContext, useCallback, useEffect } from 'react';
import get from 'lodash/get';
import find from 'lodash/find';
import set from 'lodash/set';
import styled from 'styled-components';

import FieldContainer from 'src/presentation/components/forms/FieldContainer';
import Input, { InputType as FormInputType } from 'src/presentation/components/forms/Input';
import StackContainer from '@ad-scripts/presentation/components/common/containers/stack_container';
import Textarea from 'src/presentation/components/forms/Textarea';
import { H1, H2 } from '@ad-scripts/presentation/components/common/headers';
import { Section } from 'src/presentation/components/forms/Layout';
import getErrorMessage from 'src/presentation/components/forms/getErrorMessage';
import FormContext from 'src/presentation/contexts/FormContext';
import TextInput from '@ad-scripts/presentation/components/common/text_input';
import { InputSize } from 'src/presentation/components/forms/InputSize';
import HtmlEditor from './components/HtmlEditor';

const VALID_HEX_COLOR_REGEXP = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

export enum HeaderPosition {
  INSIDE_BLOCK,
  OUTSIDE_BLOCK,
}

export enum InputType {
  COLOR_PICKER,
  NUMBER,
  TEXT_AREA_HTML,
  TEXT_AREA,
  TEXT,
}

export interface InputProps {
  size?: InputSize;
  workingState?: any;
  attr: string;
  label: string | JSX.Element;
  description?: string | JSX.Element;
  value?: undefined | null | string | number;
  defaultValue?: undefined | null | string | number;
  placeholder?: string;
  svgImage?: any;
}

const ItemContainer = styled.div``;

export const CustomRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin-bottom: 24px;

  > ${FieldContainer} {
    margin: 0 12px;
  }
  > ${FieldContainer}:first-child {
    margin-left: 0;
  }
  > ${FieldContainer}:last-child {
    margin-right: 0;
  }
`;

const HalfRow = styled(CustomRow)`
  width: 50%;
  max-width: 470px;
  ${p => (p.imageMargin ? `padding-left: ${p.imageMargin}px` : '')};
`;

const StyledRow = styled(CustomRow)`
  margin-bottom: 15px;
`;

const StyledImage = styled.img`
  margin-bottom: 30px;
  max-width: 600px;
`;

const QuickActionsStyledImage = styled(StyledImage)`
  max-width: max-content;
`;

const PrimaryHeader = styled(H1)`
  color: ${p => p.theme.colors.black};
  font-weight: ${p => p.theme.fontWeight.heavy};
  display: block;
  text-align: center;
  margin-bottom: 40px;
`;

const PrimaryDescription = styled(StackContainer).attrs({ stack: 'large', inset: 'none' })`
  color: ${p => p.theme.colors.gray700};
  font-size: 16px;
  margin-bottom: 44px;
`;

const RowLabel = styled.p``;

const MediumHeader = styled(H2)`
  color: ${p => p.theme.colors.black};
  margin-bottom: 20px;
`;

const MediumHeaderWithDescription = styled(H2)`
  color: ${p => p.theme.colors.black};
  margin-bottom: 4px;
`;

const ColumnRow = styled(CustomRow)`
  flex-shrink: 0;
  flex-direction: column;

  > div:first-child {
    margin-bottom: 15px;
  }

  > div:last-child {
    margin-left: 0;
  }
`;

const FixedWidthContentRow = styled(CustomRow)`
  margin-bottom: 5px;
  > div {
    flex-grow: 1;
    flex-basis: 0;
    margin-right: 12px;
  }

  > div:first-child {
    margin-left: 0;
  }

  > div:last-child {
    margin-right: 0;
  }
`;

const FixedWidthContentColumn = styled(FixedWidthContentRow)`
  flex-direction: column;
`;

const ControlDescription = styled.div`
  align-self: flex-start;
  font-size: ${p => p.theme.fontSize.base};
  color: ${p => p.theme.colors.gray700};
  line-height: 1.6;
  margin-bottom: ${p => p.theme.spacing.stackSmall};
  min-height: 18px;
`;

export const InputsBlock = ({
  workingState,
  size,
  inputType,
  controlsList,
}: {
  workingState: any;
  size: InputSize;
  inputType: InputType;
  controlsList: InputProps[];
}) => {
  const { errors } = useContext(FormContext);

  return controlsList.map(input => {
    const val = get(workingState, input.attr, input.defaultValue);
    return (
      <CustomRow key={input.attr} style={{ width: size }}>
        {inputType === InputType.NUMBER && (
          <Input
            data-aid={input.attr}
            key={`input.${input.attr}`}
            attr={input.attr}
            label={input.label}
            value={val}
            description={input.description}
            placeholder={input.placeholder}
            type={FormInputType.number}
          />
        )}
        {inputType === InputType.TEXT && (
          <Input
            data-aid={input.attr}
            key={`input.${input.attr}`}
            attr={input.attr}
            label={input.label}
            value={val}
            description={input.description}
            placeholder={input.placeholder}
          />
        )}
        {inputType === InputType.TEXT_AREA && (
          <Textarea
            data-aid={input.attr}
            key={`textarea.${input.attr}`}
            attr={input.attr}
            label={input.label}
            value={val}
            description={input.description}
            placeholder={input.placeholder}
          />
        )}
        {inputType === InputType.COLOR_PICKER && (
          <ColorPicker
            errors={errors}
            workingState={workingState}
            key={`color.picker.${input.attr}`}
            attr={input.attr}
            label={input.label}
            defaultValue={input.defaultValue}
            description={input.description}
          />
        )}
        {inputType === InputType.TEXT_AREA_HTML && (
          <HtmlEditorContainer>
            <HtmlEditor
              editorPath={input.attr}
              placeholder={'Enter Text...'}
              label={input.label}
              description={input.description}
            />
          </HtmlEditorContainer>
        )}
      </CustomRow>
    );
  });
};
const HtmlEditorContainer = styled.span`
  width: ${InputSize.XLARGE}px;
`;

const BlockHeaderComponent = ({ header, description }) => {
  if (!header) {
    return null;
  }
  return (
    <BlockHeaderRow>
      <MediumHeaderWithDescription>{header}</MediumHeaderWithDescription>
      {description && <ControlDescription>{description}</ControlDescription>}
    </BlockHeaderRow>
  );
};

const BlockHeaderRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
`;

export const InputsBlockHeader = React.memo(BlockHeaderComponent);

const OutsideHeader = ({ headerPosition, header, description }) => {
  if (headerPosition !== HeaderPosition.OUTSIDE_BLOCK) {
    return null;
  }
  return <InputsBlockHeader header={header} description={description} />;
};

const InsideHeader = ({ headerPosition, header, description }) => {
  if (headerPosition !== HeaderPosition.INSIDE_BLOCK) {
    return null;
  }
  return <InputsBlockHeader header={header} description={description} />;
};

const ContainerWithHeader = styled.div`
  margin-bottom: 0;
`;

export const ExtendedSection = ({
  children,
  headerPosition,
  header,
  description,
}: {
  children: any;
  svgImage?: any;
  headerPosition: HeaderPosition;
  header?: string;
  description?: any;
}) => {
  return (
    <Section>
      <OutsideHeader headerPosition={headerPosition} header={header} description={description} />
      <FixedWidthContentRow>{children}</FixedWidthContentRow>
    </Section>
  );
};

export const DefaultSection = ({
  children,
  svgImage,
  headerPosition,
  header,
  description,
  imageMargin,
}: {
  children: any;
  svgImage?: any;
  imageMargin?: number;
  headerPosition: HeaderPosition;
  header?: string;
  description?: string | ReactElement;
}) => {
  return (
    <ExtendedSection headerPosition={headerPosition} header={header} description={description}>
      <HalfRow>
        <ContainerWithHeader>
          <InsideHeader headerPosition={headerPosition} header={header} description={description} />
          {children}
        </ContainerWithHeader>
      </HalfRow>
      {svgImage && (
        <HalfRow imageMargin={imageMargin}>
          <StyledImage src={svgImage} />
        </HalfRow>
      )}
    </ExtendedSection>
  );
};

const StyledColorBox = styled.div`
  margin-right: 0;
  display: block;
  width: 30px;
  height: 30px;
  border-radius: 5px;
  border: 1px ${p => (!p.isError ? 'silver' : p.theme.colors.red400)} solid;
  background-color: ${p => (VALID_HEX_COLOR_REGEXP.test(p.bgColor) ? p.bgColor : p.defaultValue)};
`;

const colorBoxControl = ({ bgColor, defaultValue, isError }) => {
  return <StyledColorBox bgColor={bgColor} defaultValue={defaultValue} isError={isError} />;
};
const ColorBox = React.memo(colorBoxControl);

export const ColorBoxRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;

  > div {
    margin: 0;
  }
  > div:first-child {
    margin-right: 3px;
    margin-top: 1px;
  }
`;

const ColorPickerInput = ({ color, attr, onColorChanged, error }) => {
  const onChange = useCallback(
    event => {
      const { value } = event.target;
      onColorChanged(value);
    },
    [onColorChanged]
  );
  return (
    <StyledInputBox>
      <TextInput className={error && 'invalid'} data-aid={attr} value={color} onChange={onChange} isError={!!error} />
      {error && (
        <ErrorWrapper>
          <ErrorLabel data-aid={`${attr}-error`}>{getErrorMessage(error, '')}</ErrorLabel>
        </ErrorWrapper>
      )}
    </StyledInputBox>
  );
};

const StyledInputBox = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ErrorWrapper = styled.div`
  padding-top: 4px;
  position: relative;
`;

const ErrorLabel = styled.div`
  position: absolute;
  min-width: 350px;
  color: ${p => p.theme.colors.red400};
`;

export const ColorPicker = ({ workingState, errors, attr, label, defaultValue, description }) => {
  const { updateState } = useContext(FormContext);
  const color = get(workingState, attr, defaultValue);
  const error = find(errors, error => error.attr === attr);

  const onColorChanged = useCallback(
    value => {
      updateState(set({}, attr, value || ''));
    },
    [updateState, attr]
  );

  return (
    <FieldContainer
      label={label}
      description={description}
      data-aid={attr}
      control={
        <ColorBoxRow>
          <ColorBox bgColor={color} defaultValue={defaultValue} isError={!!error} />
          <ColorPickerInput attr={attr} color={color} onColorChanged={onColorChanged} error={error} />
        </ColorBoxRow>
      }
    />
  );
};

export const ScrollToFirstError = ({ errors }) => {
  const getFirstInvalidElement = useCallback(() => {
    if (errors.length) {
      return document.querySelector('.invalid');
    }
  }, [errors]);

  useEffect(() => {
    if (errors.length) {
      const invalidElement = getFirstInvalidElement();
      invalidElement && invalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [errors, getFirstInvalidElement]);

  return null;
};

export const SelectorContainer = styled.div`
  width: 100%;
`;

export const EndChatStyledImage = styled(StyledImage)`
  padding-left: 150px;
  max-width: fit-content;
`;

export {
  ColumnRow,
  FixedWidthContentRow,
  FixedWidthContentColumn,
  HalfRow,
  ItemContainer,
  MediumHeader,
  PrimaryDescription,
  PrimaryHeader,
  QuickActionsStyledImage,
  RowLabel,
  StyledImage,
  StyledRow,
};
